import { OperationType, SafeTransaction } from '@polymarket/builder-relayer-client';
import { createPublicClient, encodeFunctionData, erc20Abi, fallback, http } from 'viem';
import { polygon } from 'viem/chains';

export const POLYGON_RPC_URL = 'https://polygon-bor-rpc.publicnode.com';
const ALCHEMY_BEARER_TOKEN =
  process.env.NEXT_PUBLIC_ALCHEMY_BEARER_TOKEN || 'T7bAMb2Gq3a6hknoxsEpo';

import {
  COLLATERAL_OFFRAMP_ADDRESS,
  COLLATERAL_ONRAMP_ADDRESS,
  COMBO_EXCHANGE_V3_ADDRESS,
  COMBO_POSITION_MANAGER_ADDRESS,
  CTF_COLLATERAL_ADAPTER_ADDRESS,
  CTF_CONTRACT_ADDRESS,
  CTF_EXCHANGE_ADDRESS,
  NEG_RISK_ADAPTER_ADDRESS,
  NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS,
  NEG_RISK_CTF_EXCHANGE_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

const MAX_UINT256 =
  '115792089237316195423570985008687907853269984665640564039457584007913129639935';

const erc1155Abi = [
  {
    inputs: [
      { name: 'operator', type: 'address' },
      { name: 'approved', type: 'bool' },
    ],
    name: 'setApprovalForAll',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'operator', type: 'address' },
    ],
    name: 'isApprovedForAll',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

export const publicClient = createPublicClient({
  chain: polygon,
  // Alchemy first (authenticated, far more reliable), publicnode as a fallback if
  // Alchemy is unreachable/rate-limited — avoids a single flaky public RPC breaking
  // every balance/approval read (see useEmbeddedWalletMigration "Failed to fetch").
  transport: fallback([
    http(`https://polygon-mainnet.g.alchemy.com/v2/${ALCHEMY_BEARER_TOKEN}`),
    http(POLYGON_RPC_URL),
  ]),
});

//

// Spenders that need pUSD approval (V2 exchange addresses via updated constants).
// Combo Exchange V3 is included so the standard setup flow also enables combo
// (RFQ) BUYs, which settle pUSD on Exchange v3 rather than the CTF exchanges.
const PUSD_SPENDERS = [
  { address: CTF_CONTRACT_ADDRESS, name: 'CTF Contract' },
  { address: NEG_RISK_ADAPTER_ADDRESS, name: 'Neg Risk Adapter' },
  { address: CTF_EXCHANGE_ADDRESS, name: 'CTF Exchange V2' },
  { address: NEG_RISK_CTF_EXCHANGE_ADDRESS, name: 'Neg Risk CTF Exchange V2' },
  { address: COLLATERAL_OFFRAMP_ADDRESS, name: 'Collateral Offramp' },
  { address: CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'CTF Collateral Adapter' },
  { address: NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS, name: 'Neg Risk CTF Collateral Adapter' },
  { address: COMBO_EXCHANGE_V3_ADDRESS, name: 'Combo Exchange V3' },
] as const;

// Spenders that need USDC.e approval (specifically the migration onramp for V2)
const USDC_E_SPENDERS = [
  { address: COLLATERAL_ONRAMP_ADDRESS, name: 'Collateral Bridge (Migration)' },
] as const;

// ERC-1155 operator approvals. Most operate on the CTF contract; combo SELLs
// need the Combo PositionManager to approve Exchange v3, so each entry carries
// its own token contract rather than assuming CTF.
const OUTCOME_TOKEN_APPROVALS = [
  { token: CTF_CONTRACT_ADDRESS, operator: CTF_EXCHANGE_ADDRESS, name: 'CTF Exchange' },
  {
    token: CTF_CONTRACT_ADDRESS,
    operator: NEG_RISK_CTF_EXCHANGE_ADDRESS,
    name: 'Neg Risk Exchange',
  },
  { token: CTF_CONTRACT_ADDRESS, operator: NEG_RISK_ADAPTER_ADDRESS, name: 'Neg Risk Adapter' },
  {
    token: CTF_CONTRACT_ADDRESS,
    operator: CTF_COLLATERAL_ADAPTER_ADDRESS,
    name: 'CTF Collateral Adapter',
  },
  {
    token: CTF_CONTRACT_ADDRESS,
    operator: NEG_RISK_CTF_COLLATERAL_ADAPTER_ADDRESS,
    name: 'Neg Risk CTF Collateral Adapter',
  },
  {
    token: COMBO_POSITION_MANAGER_ADDRESS,
    operator: COMBO_EXCHANGE_V3_ADDRESS,
    name: 'Combo PositionManager → Exchange V3',
  },
] as const;

const checkAllowanceForToken = async (
  tokenAddress: string,
  safeAddress: string,
  spender: string,
): Promise<boolean> => {
  try {
    const allowance = await publicClient.readContract({
      address: tokenAddress as `0x${string}`,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [safeAddress as `0x${string}`, spender as `0x${string}`],
      authorizationList: undefined,
    });

    const threshold = BigInt('1000000000000');
    return allowance >= threshold;
  } catch (error) {
    console.error(
      `[Approvals] Failed to check allowance for token ${tokenAddress} / spender ${spender}:`,
      error,
    );
    return false;
  }
};

const checkERC1155ApprovalForSpender = async (
  safeAddress: string,
  spender: string,
  tokenContract: string = CTF_CONTRACT_ADDRESS,
): Promise<boolean> => {
  try {
    const isApproved = await publicClient.readContract({
      address: tokenContract as `0x${string}`,
      abi: erc1155Abi,
      functionName: 'isApprovedForAll',
      args: [safeAddress as `0x${string}`, spender as `0x${string}`],
      authorizationList: undefined,
    });

    return isApproved;
  } catch (error) {
    console.error(`[Approvals] Failed to check ERC1155 status for ${spender}:`, error);
    return false;
  }
};

export const checkAllApprovals = async (
  safeAddress: string,
): Promise<{
  allApproved: boolean;
  usdcApprovals: Record<string, boolean>;
  outcomeTokenApprovals: Record<string, boolean>;
}> => {
  const usdcApprovals: Record<string, boolean> = {}; // UI still calls it usdcApprovals for compatibility
  const outcomeTokenApprovals: Record<string, boolean> = {};

  await Promise.all(
    PUSD_SPENDERS.map(async ({ address, name }) => {
      usdcApprovals[name] = await checkAllowanceForToken(
        PUSD_CONTRACT_ADDRESS,
        safeAddress,
        address,
      );
    }),
  );

  await Promise.all(
    USDC_E_SPENDERS.map(async ({ address, name }) => {
      // Logic for UI compatibility: we just add them to the same pool for "allApproved"
      usdcApprovals[name] = await checkAllowanceForToken(
        USDC_E_CONTRACT_ADDRESS,
        safeAddress,
        address,
      );
    }),
  );

  await Promise.all(
    OUTCOME_TOKEN_APPROVALS.map(async ({ token, operator, name }) => {
      outcomeTokenApprovals[name] = await checkERC1155ApprovalForSpender(
        safeAddress,
        operator,
        token,
      );
    }),
  );

  const allApproved =
    Object.values(usdcApprovals).every((approved) => approved) &&
    Object.values(outcomeTokenApprovals).every((approved) => approved);

  return {
    allApproved,
    usdcApprovals,
    outcomeTokenApprovals,
  };
};

export const createAllApprovalTxs = (): SafeTransaction[] => {
  const safeTxns: SafeTransaction[] = [];

  // V2: Approve pUSD (new collateral) instead of USDC.e to the exchange contracts
  for (const { address } of PUSD_SPENDERS) {
    safeTxns.push({
      to: PUSD_CONTRACT_ADDRESS,
      operation: OperationType.Call,
      data: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'approve',
        args: [address as `0x${string}`, BigInt(MAX_UINT256)],
      }),
      value: '0',
    });
  }

  // Migration: Approve USDC.e to the Collateral Onramp
  for (const { address } of USDC_E_SPENDERS) {
    safeTxns.push({
      to: USDC_E_CONTRACT_ADDRESS,
      operation: OperationType.Call,
      data: encodeFunctionData({
        abi: erc20Abi,
        functionName: 'approve',
        args: [address as `0x${string}`, BigInt(MAX_UINT256)],
      }),
      value: '0',
    });
  }

  for (const { token, operator } of OUTCOME_TOKEN_APPROVALS) {
    safeTxns.push({
      to: token,
      operation: OperationType.Call,
      data: encodeFunctionData({
        abi: erc1155Abi,
        functionName: 'setApprovalForAll',
        args: [operator as `0x${string}`, true],
      }),
      value: '0',
    });
  }

  return safeTxns;
};
